import type { DestinationTranslationValue } from '@/shared/api/types/destination-types'
import { useTranslation } from 'react-i18next'
import { LocalizedTranslationJobSection } from '@/features/localized-translation'
import { useLanguageLabel } from '@/shared/hooks/use-language-label'
import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'
import { ReasonType } from '@/shared/constants/translation-status'

interface DestinationTranslationSectionProps {
  /** Danh sách ngôn ngữ được cấu hình và hiển thị thành các tab trong editor. */
  languages: string[]
  /** Danh sách bản dịch name/description của Destination do form cha quản lý. */
  translations: DestinationTranslationValue[]
  /** Hàm trả về bản dịch Destination mới sau khi user sửa hoặc sau khi job nền hoàn tất. */
  onChange: (translations: DestinationTranslationValue[]) => void
  /** Báo cho form cha biết còn nội dung đã sửa nhưng chưa tạo lại bản dịch. */
  onGenerationStateChange?: (hasPendingGeneration: boolean) => void
  /** Id Destination hoặc draft id ổn định dùng để ghép job nền về đúng form. */
  entityId: string
  /** Tên hiển thị tùy chọn trong toast hoặc background jobs panel. */
  entityLabel?: string
  /** Đường dẫn tùy chọn để mở lại Destination từ background jobs panel. */
  entityPath?: string
  /** Khóa chỉnh sửa và ẩn workflow dịch, nhưng vẫn cho xem nội dung theo ngôn ngữ. */
  readonly?: boolean
}

/**
 * Lớp bọc cấu hình workflow dịch nền dùng chung cho Destination.
 */
export function DestinationTranslationSection({
  languages,
  translations,
  onChange,
  onGenerationStateChange,
  entityId,
  entityLabel,
  entityPath,
  readonly = false,
}: DestinationTranslationSectionProps) {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()

  return (
    <LocalizedTranslationJobSection<DestinationTranslationValue>
      languages={languages}
      translations={translations}
      onChange={onChange}
      scope={TRANSLATION_JOB_SCOPE.DESTINATION}
      entityId={entityId}
      {...(entityLabel ? { entityLabel } : {})}
      {...(entityPath ? { entityPath } : {})}
      audioEnabled={ReasonType.TRANSLATE_AUDIO_NOT_AVAILABLE}
      requiredMessage={t('destination.translationRequired')}
      readonly={readonly}
      {...(onGenerationStateChange ? { onGenerationStateChange } : {})}
      fields={(activeLanguage) => [
        {
          key: 'title',
          label: t('common.title', { defaultValue: 'Title' }),
          placeholder: t('destination.translationTitlePlaceholder', {
            language: languageLabel(activeLanguage),
          }),
          required: false,
        },
        {
          key: 'name',
          label: t('common.name'),
          placeholder: t('poi.translationNamePlaceholder', {
            language: languageLabel(activeLanguage),
          }),
        },
        {
          key: 'description',
          label: t('common.description'),
          placeholder: t('poi.translationDescriptionPlaceholder', {
            language: languageLabel(activeLanguage),
          }),
          multiline: true,
          rows: 5,
        },
      ]}
      translateLabel={t('poi.localizedSpeech.generateSelectedTextOnly')}
      scopeHint={t('poi.localizedSpeech.scopeHint')}
      loadingText={t('poi.localizedSpeech.generatingTextOnly')}
    />
  )
}
